<template>
  <div>
    <v-tabs v-model="tab" background-color="primary" dark>
      <v-tab>
        <v-icon style="margin-right: 7px">mdi-bowl-mix</v-icon>
        {{ $t("meals") }}
      </v-tab>
      <v-tab>
        <v-icon style="margin-right: 7px">mdi-domain</v-icon>
        {{ $t("companies") }}
      </v-tab>
      <v-tab>
        <v-icon style="margin-right: 7px">mdi-shopping-outline</v-icon>
        {{ $t("pak") }}
      </v-tab>
    </v-tabs>

    <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
      <v-row align="start" class="my-2">
        <v-col cols="auto" class="d-flex align-center">
          <shift-picker v-model="selectedShifts" multiple />
        </v-col>
      </v-row>

      <report-all-companies-tab-packing v-if="isPackingTab" :selected-shifts="selectedShifts" />

      <v-data-table
        v-else
        :items="customSearch(visibleRows, headers, filters.search)"
        :loading="loading"
        :headers="getMobileHeaders(headers)"
        class="striped-table ordera-box-shadow"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
      >
        <template v-slot:item.name="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.name }}</span>
            </template>
            <span>{{ item.id }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.sources="{ item }">
          <v-chip v-for="source in item.sources" :key="source" x-small class="mr-1">
            {{ sourceLabel(source) }}
          </v-chip>
        </template>
        <template v-slot:body.append>
          <tr>
            <td v-for="header in getMobileHeaders(headers)" :key="header.value" style="font-weight: bold">
              <template v-if="header.value === 'name'">{{ $t("total") }}</template>
              <template v-else-if="isCountColumn(header.value)">{{ totals[header.value] }}</template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import ReportAllCompaniesTabPacking from "@/components/report/all-companies/tabs/ReportAllCompaniesTabPacking.vue";
import { SHIFTS } from "@/constants/shifts";

/**
 * "Sve firme – zbirni izvještaj": obroci svih firmi restorana iz orders
 * i orders_more_restaurants (backend sabira), po stavci i po firmi.
 * Kartica "Pakovanje" ima svoje učitavanje, štampu i Excel.
 */
export default {
  name: "ReportTabAllCompaniesSummary",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: { ReportAllCompaniesTabPacking },
  data: () => ({
    tab: 0,
    loading: false,
    summary: { items: [], companies: [] },
    selectedShifts: [1, 2, 3],
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    isCompaniesTab() {
      return this.tab === 1;
    },
    isPackingTab() {
      return this.tab === 2;
    },
    selectedShiftColumns() {
      return SHIFTS.filter((shift) => this.selectedShifts.includes(shift.id));
    },
    /** Redovi aktivne kartice; ukupno se računa samo za izabrane smjene. */
    visibleRows() {
      const rows = this.isCompaniesTab ? this.summary.companies : this.summary.items;

      return rows
        .map((row) => {
          const visible = { ...row };
          SHIFTS.forEach(({ id, key }) => {
            visible[key] = this.selectedShifts.includes(id) ? row[key] : 0;
          });
          visible.total = this.selectedShiftColumns.reduce((sum, { key }) => sum + row[key], 0);
          return visible;
        })
        .filter((row) => row.total > 0)
        .map((row, index) => ({ ...row, num: index + 1 }));
    },
    totals() {
      const keys = [...SHIFTS.map((shift) => shift.key), "total"];
      return keys.reduce((acc, key) => {
        acc[key] = this.visibleRows.reduce((sum, row) => sum + (row[key] || 0), 0);
        return acc;
      }, {});
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t(this.isCompaniesTab ? "company" : "meal"), value: "name" },
        ...(this.isCompaniesTab ? [{ text: "", value: "sources", sortable: false, print: false }] : []),
        ...this.selectedShiftColumns.map((shift) => ({ text: this.$t(shift.label), value: shift.key })),
        { text: this.$t("total"), value: "total" },
      ];
    },
  },
  methods: {
    async load() {
      if (!this.restaurantId || !this.filters.dates.from) {
        return;
      }
      this.loading = true;
      try {
        const { data } = await reportService.getAllCompaniesSummary(this.restaurantId, {
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        });
        this.summary = {
          items: data?.items || [],
          companies: (data?.companies || []).map((company) => ({
            ...company,
            name: this.toLatin(company.name || ""),
          })),
        };
      } catch (error) {
        this.summary = { items: [], companies: [] };
        this.showErrorSnackbar(error, this.$t("error_loading_meals"));
      } finally {
        this.loading = false;
      }
    },
    isCountColumn(value) {
      return SHIFTS.some((shift) => shift.key === value) || value === "total";
    },
    sourceLabel(source) {
      return this.$t(source === "more_restaurants" ? "more_restaurants" : "meal_in_meals");
    },
    tableForExport() {
      const headers = this.headers.filter((header) => header.print !== false);
      const items = [
        ...this.customSearch(this.visibleRows, this.headers, this.filters.search),
        { num: "", name: this.$t("total"), ...this.totals },
      ];
      return { headers, items };
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
    },
    restaurantId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    "$store.state.report.reload"() {
      this.load();
    },
    isPrinting() {
      if (this.isPrinting && !this.isPackingTab) {
        const title = this.$t("all_companies_summary_report");
        this.printDocument({
          tables: [{ ...this.tableForExport(), showCaption: true }],
          title: [title, this.$t(this.isCompaniesTab ? "companies" : "meals")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading && !this.isPackingTab) {
        const title = this.$t("all_companies_summary_report");
        const name = this.$t(this.isCompaniesTab ? "companies" : "meals");
        this.downloadExcel({
          fileName: `${title} ${name}.xlsx`,
          sheets: [{ name, ...this.tableForExport(), title, subtitle: this.subtitle }],
        });
      }
    },
  },
};
</script>
